import {
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  UpdateIconButton,
} from "@compass/core";

import { ListBeProperty } from "components";
import {
  Permission,
  PermissionAction,
  SortObject,
  StudentServiceIndexListDto,
  StudentServiceIndexListResource,
} from "generated/openApi";

import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import { studentServiceIndexPageRoute } from "../StudentServiceIndexPage/StudentServiceIndexPage";
import { useContext } from "react";
import { StudentServiceIndexListContext, StudentServiceIndexListContextValue } from "./StudentServiceIndexListPage";

export const StudentServiceIndexTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  studentServiceIndexes: StudentServiceIndexListDto[];
  listTotal: number | undefined;
  resource: StudentServiceIndexListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  loadList: () => void;
  selectMode: boolean;
}) => {
  const navigate = useNavigate();
  const studentServiceIndexListContext =
    useContext<StudentServiceIndexListContextValue>(StudentServiceIndexListContext);

  const properties = props.resource.properties || [];

  const handleSelect = (id: number) => {
    if (!props.selectMode) {
      navigate({
        pathname: studentServiceIndexPageRoute.getPath({
          id: id,
          crudMode: "view",
          screenType: "dialog",
        }),
      });
    } else {
      if (window.confirm("Are you sure you want to select this item?")) {
        studentServiceIndexListContext.onSelectIndex?.(id);
        navigate("../");
      }
    }
  };

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("activity"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("serviceType"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find(
                  (x) => x.propertyName === pn<StudentServiceIndexListDto>("serviceSubType")
                )}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            {/* <TableCell>
              <ListBeProperty
                listProperty={properties.find(
                  (x) => x.propertyName === pn<StudentServiceIndexListDto>("serviceSubType")
                )}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell> */}
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("name"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("duration"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find(
                  (x) => x.propertyName === pn<StudentServiceIndexListDto>("serviceLocation")
                )}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("provider"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find(
                  (x) => x.propertyName === pn<StudentServiceIndexListDto>("serviceDescription")
                )}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceIndexListDto>("note"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            {!props.selectMode && <TableCell></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.studentServiceIndexes.map((studentServiceIndexItem) => (
            <TableRow key={studentServiceIndexItem.id} onClick={() => handleSelect(studentServiceIndexItem.id)}>
              <TableCell>{studentServiceIndexItem.activity}</TableCell>
              <TableCell>
                <Stack direction="row" spacing="sm" alignItems="center">
                  <Typography variant="inherit">{studentServiceIndexItem.serviceType}</Typography>
                  {studentServiceIndexItem?.isForParent && <EscalatorWarningIcon />}
                </Stack>
              </TableCell>
              <TableCell>{studentServiceIndexItem.serviceSubType}</TableCell>
              <TableCell>{studentServiceIndexItem.name}</TableCell>
              <TableCell>{studentServiceIndexItem.duration}</TableCell>
              <TableCell>{studentServiceIndexItem.serviceLocation}</TableCell>
              <TableCell>{studentServiceIndexItem.provider}</TableCell>
              <TableCell>{studentServiceIndexItem.serviceDescription}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography color="error" variant="inherit">{studentServiceIndexItem.note}</Typography>
              </TableCell>

              {!props.selectMode && (
                <TableCell type="action">
                  {isAuthorized(props.rootPermission, PermissionAction.Update) && (
                    <UpdateIconButton
                      onClick={() =>
                        navigate({
                          pathname: studentServiceIndexPageRoute.getPath({
                            id: studentServiceIndexItem.id,
                            crudMode: "edit",
                            screenType: "dialog",
                          }),
                        })
                      }
                    />
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
